export const contentPlan = {
  title: 'Content Plan',
  subtitle: 'Generate a content plan from a keyword research',
  keywordResearch: {
    placeholder: 'Select a keyword research...',
    empty: 'No keyword researches available',
  },
  generate: {
    button: 'Generate Plan',
    generating: 'Generating...',
    hint: 'This may take a few minutes',
  },
  status: {
    pending: 'Pending',
    generating: 'Generating...',
    completed: 'Completed',
    failed: 'Failed',
  },
  list: {
    emptyTitle: 'No content plans yet',
    emptyDescription: 'Select keywords from a keyword research to create a content plan.',
    goToKeywordResearch: 'Go to Keyword Research',
    importFromCsv: 'Import from CSV',
    columns: {
      name: 'Name',
      status: 'Status',
      keywords: 'Keywords',
      contents: 'Contents',
      approval: 'Approval',
      created: 'Created',
    },
    deleteDialog: {
      title: 'Delete content plan',
      description: 'This action cannot be undone. The content plan and all its items will be permanently removed.',
      cancel: 'Cancel',
      confirm: 'Delete',
      deleting: 'Deleting...',
    },
    renameDialog: {
      title: 'Rename content plan',
      label: 'Plan name',
      cancel: 'Cancel',
      confirm: 'Save',
      saving: 'Saving...',
    },
  },
  toast: {
    createSuccess: 'Content plan generation started',
    downloadSuccess: 'Content plan downloaded',
    deleteSuccess: 'Content plan deleted',
    renameSuccess: 'Content plan renamed',
    itemDeleteSuccess: 'Item removed from plan',
    bulkItemDeleteSuccess: '{{count}} items removed from plan',
    bulkTypeUpdateSuccess: 'Type changed for {{count}} items',
    retryLinkedinSuccess: 'LinkedIn search completed',
    removeLinkedinPostSuccess: 'LinkedIn post removed',
    updateSuccess: 'Item updated',
    approveSuccess: 'Item approved',
    rejectSuccess: 'Item rejected',
  },
  errors: {
    generateFailed: 'Failed to generate content plan',
    downloadFailed: 'Failed to download content plan',
    deleteFailed: 'Failed to delete content plan',
    renameFailed: 'Failed to rename content plan',
    itemDeleteFailed: 'Failed to remove item',
    bulkItemDeleteFailed: 'Failed to remove items',
    bulkTypeUpdateFailed: 'Failed to change item type',
    retryLinkedinFailed: 'Failed to retry LinkedIn search',
    removeLinkedinPostFailed: 'Failed to remove LinkedIn post',
    noKeywords: 'No keywords to generate the plan',
    updateFailed: 'Failed to update item',
    missingParams: 'Missing required parameters',
    loadKeywordsFailed: 'Failed to load keyword details',
  },
  confirmation: {
    subtitle: '{{count}} keywords selected to plan',
    back: 'Back',
    loadingKeywords: 'Loading keyword details...',
    settingsTitle: 'Plan settings',
    planName: 'Plan Name',
    planNamePlaceholder: 'e.g. March 2026 Plan',
    planNameHelper: 'Choose a descriptive name to identify this plan later',
    context: {
      instructionsLabel: 'General instructions (optional)',
      instructionsPlaceholder:
        'e.g. Content for Mexican SMBs; labor-law topics must cite the 2026 reform; do not cover university degrees',
      instructionsHelper: 'Guidance applied to every content piece in this plan',
      excludeRankedLabel: 'Exclude keywords already in top 10',
      excludeRankedHelper:
        "By default, keywords your site already ranks in Google's top 10 for are skipped. Turn this off to generate content for them anyway.",
      focusTitle: 'Focus per keyword (optional)',
      focusHelper:
        'Select keywords and assign a shared focus, or set one per keyword — used to disambiguate intent and add constraints',
      focusLabel: 'Focus',
      focusPlaceholder:
        'e.g. interior decoration, NOT the university degree; keep to 2025 regulation',
      selectAll: 'Select all',
      selectedCount: '{{count}} selected',
      selectAllResults: 'Select all results ({{count}})',
      applyToSelection: 'Assign focus to selection ({{count}})',
      applyToCollection: 'Focus collection ({{count}})',
      searchPlaceholder: 'Filter keywords...',
      noSearchResults: 'No keywords match your filter',
      noKeywords: 'No keywords to show',
      apply: 'Apply',
      removeFocus: 'Remove focus',
      charCount: '{{current}}/{{max}}',
      ungroupedGroup: 'Ungrouped',
      groupCount: '{{count}} keywords',
      selectGroup: 'Select all keywords in {{name}}',
    },
  },
  csvImport: {
    pageTitle: 'Import Content Plan',
    pageSubtitle: 'Bring your existing plan into Referent in three steps',
    rowsPageTitle: 'Import rows from CSV',
    rowsPageSubtitle: 'Add rows to your plan in three steps',
    stepper: {
      upload: 'Upload',
      mapping: 'Map',
      preview: 'Review',
    },
    steps: {
      upload: {
        title: 'Upload your CSV',
        descriptionCreate: 'Name your plan and attach the file.',
        descriptionAppend: 'Attach the file.',
        templateTitle: "Don't have a file ready?",
        templateDescription: 'Download the template with the correct columns and an example row.',
        downloadTemplate: 'Download template',
        planNameLabel: 'Plan name',
        planNamePlaceholder: 'March 2026 Plan',
        dropzonePrimary: 'Drop your CSV here',
        dropzoneSecondary: 'or click to browse',
        dropzoneHint: '.csv up to 10 MB',
        parsing: 'Parsing your file...',
        continue: 'Continue',
      },
      mapping: {
        title: 'Map your columns',
        description: 'Match each plan field with a column from your CSV',
        requiredSection: 'Required fields',
        optionalSection: 'Optional fields',
        requiredMark: '*',
        mappedBadge: 'Mapped',
        noneOption: '— None —',
        duplicateHeader: 'Column already used in another field',
        preview: 'Preview rows',
        back: 'Back',
      },
      preview: {
        title: 'Review rows',
        description: 'Valid rows are preselected. Uncheck any you want to skip.',
        statsValid: 'Valid',
        statsInvalid: 'Invalid',
        statsTotal: 'Total rows',
        tabAll: 'All',
        tabValid: 'Valid',
        tabInvalid: 'Invalid',
        columnRow: 'Row',
        columnMainKeyword: 'Main Keyword',
        columnTitle: 'Title',
        columnType: 'Type',
        columnVolume: 'Volume',
        columnStatus: 'Status',
        importButton: 'Import {{count}} rows',
        back: 'Back',
        importing: 'Importing...',
        emptySelection: 'Select at least one valid row to continue',
      },
    },
    fields: {
      mainKeyword: 'Main Keyword',
      title: 'Title',
      type: 'Type',
      contentApproach: 'Content Approach',
      interactionGroup: 'Interaction Group',
      aiFocusFactor: 'AI Focus Factor',
      volume: 'Volume',
      category: 'Category',
      secondaryKeywords: 'Secondary Keywords',
      entities: 'Entities',
      relatedPrompts: 'Related Prompts',
      deadline: 'Deadline',
    },
    dialog: {
      title: 'Import rows from CSV',
      triggerButton: 'Import rows',
    },
    toast: {
      createdSuccess: 'Content plan created with {{count}} items',
      appendedSuccess: '{{count}} rows added to the plan',
      error: 'Import failed — please try again',
    },
    rowErrors: {
      REQUIRED_EMPTY: 'Required field is empty',
      INVALID_ENUM: 'Invalid value "{{value}}" — expected: {{expected}}',
      INVALID_INTEGER: 'Not a valid integer',
      INVALID_DATE: 'Invalid date format — expected YYYY-MM-DD',
    },
    apiErrors: {
      CONTENT_PLAN_CSV_IMPORT_PARSE_ERROR:
        'We could not read the CSV file. Make sure it is a valid CSV and try again.',
      CONTENT_PLAN_CSV_IMPORT_EMPTY: 'The file has no data rows.',
      CONTENT_PLAN_CSV_IMPORT_INVALID_PAYLOAD:
        'The request is invalid. Reload the page and try again.',
      CONTENT_PLAN_CSV_IMPORT_INVALID_FIELD_MAP:
        'Check the column mapping: some fields are unassigned or columns are duplicated.',
      CONTENT_PLAN_CSV_IMPORT_NO_VALID_ROWS: 'No valid rows selected to import.',
      CONTENT_PLAN_CSV_IMPORT_CREATION_FAILED:
        'We could not create the content plan. Try again.',
      generic: 'Something went wrong with the import. Try again.',
    },
  },
  progress: {
    title: 'Generating your content plan',
    steps: {
      analyzing_serp: 'Analyzing SERPs',
      filtering: 'Filtering already ranked keywords',
      researching: 'Researching LLM responses, AI sub-queries & LinkedIn',
      expanding_keywords: 'Expanding secondary keywords',
      generating: 'Generating content plan',
      verifying: 'Verifying competitor mentions',
    },
    excludedKeywords: '{{count}} keywords excluded (already ranked in top 10)',
    excludedReason: 'Reason',
    skippedReasons: {
      filtering: 'No company website configured',
    },
  },
  result: {
    subtitle: '{{count}} content pieces generated',
    exportCsv: 'Download CSV',
    columns: {
      mainKeyword: 'Main Keyword',
      title: 'Title',
      type: 'Type',
      contentApproach: 'Approach',
      interactionGroup: 'Interaction',
      aiFocusFactor: 'AI Focus',
      volume: 'Volume',
      category: 'Category',
      secondaryKeywords: 'Secondary Keywords',
      entities: 'Entities',
      relatedPrompts: 'Related Prompts',
      deadline: 'Deadline',
      linkedinPosts: 'LinkedIn Posts',
      action: 'Action',
      status: 'Status',
    },
    saveChanges: 'Save Changes',
    noCategory: 'No category',
    editCategory: 'Edit category',
    noLinkedinPosts: 'No LinkedIn posts found',
    retryLinkedin: 'Retry LinkedIn search',
    removeLinkedinPost: 'Remove LinkedIn post',
    create: 'Create',
    created: 'Created',
    bulkSelected: '{{count}} items selected',
    bulkMixedWarning: 'Select only blogs or only landings',
    bulkCreateBlogs: 'Create {{count}} Blogs',
    bulkCreateLandings: 'Create {{count}} Landings',
    bulkDeleteSelected: 'Delete selected',
    bulkAddTag: 'Add tag',
    bulkChangeType: 'Change type',
    edit: 'Edit',
    deleteItem: 'Delete item',
    deleteDialog: {
      title: 'Delete item',
      description: 'This item will be removed from the plan.',
      cancel: 'Cancel',
      confirm: 'Delete',
    },
    bulkDeleteDialog: {
      title: 'Delete selected items',
      description: '{{count}} items will be removed from the plan. This action cannot be undone.',
    },
    hintCommaSeparated: 'Comma-separated',
    hintOnePerLine: 'One per line',
    tags: {
      addTag: 'Add tag',
      edit: 'Edit',
      searchTags: 'Search tags...',
      noTagsFound: 'No results',
      noTagsAvailable: 'No tags available',
      createNewTag: 'Create new tag',
      tagNamePlaceholder: 'Tag name',
      tagColor: 'Color:',
      cancel: 'Cancel',
      create: 'Create',
      systemTagTooltip: 'Assigned automatically when the content is drafted',
      bulkAssignSuccess: 'Tag added to {{count}} items',
      createFailed: 'Failed to create tag',
      removeTag: 'Remove tag {{name}}',
    },
  },
  trendOrigin: {
    label: 'From a trend opportunity',
    direction: {
      rising: 'Rising',
      seasonal: 'Seasonal',
      flat: 'Flat',
      declining: 'Declining',
      unknown: 'Unknown trend',
    },
    discoveredOn: 'Discovered {{date}}',
  },
  detail: {
    backToList: 'Back to plans',
    errorTitle: 'Failed to load plan',
    errorDescription: 'Could not load the contents of this plan.',
  },
  failed: {
    title: 'Generation Failed',
    message: 'An error occurred while generating the content plan.',
    retry: 'Retry',
    backToList: 'Back to plans',
  },
  itemStatus: {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
  },
  filters: {
    search: 'Search by keyword or title...',
    status: 'Status',
    type: 'Type',
    typeBlog: 'Blog',
    typeLanding: 'Landing',
    tags: 'Tags',
    all: 'All',
    noResults: 'No content items match your filters',
  },
  findReplace: {
    toggle: 'Find & replace',
    searchPlaceholder: 'Term to find...',
    replacePlaceholder: 'Replace with...',
    matchCase: 'Match case',
    replace: 'Replace',
    replaceCurrentAriaLabel: 'Replace the current match',
    replaceAll: 'Replace all',
    requiredFieldWarning: 'A required field cannot be emptied. The replacement was skipped.',
    counter: '{{current}}/{{total}}',
    previousMatch: 'Previous match',
    nextMatch: 'Next match',
    close: 'Close',
    noMatches: 'No matches in the plan',
    confirm: {
      title: 'Replace across the plan',
      description:
        'The term will be replaced across the entire plan — including rows hidden by your current filters: about {{occurrences}} matches across {{rows}} rows.',
      note: 'Matches in required fields are skipped when they would leave the field empty, so the final total may be lower.',
      warning: 'This action cannot be undone.',
      cancel: 'Cancel',
      confirm: 'Replace all',
      replacing: 'Replacing...',
    },
    result: {
      summary: '{{items}} rows updated ({{occurrences}} matches replaced).',
      skippedNote:
        'Some required fields were left unchanged to avoid emptying them ({{rows}}).',
    },
    toast: {
      success: 'Term replaced in {{rows}} rows',
    },
    errors: {
      failed: 'Failed to replace the term',
    },
  },
  approval: {
    approve: 'Approve',
    reject: 'Reject',
    summary: {
      approved: '{{count}} approved',
      pending: '{{count}} pending',
      rejected: '{{count}} rejected',
    },
  },
} as const
